import { useEffect, useState } from 'react';

import {
  isAbleToSeeUIElement,
  AdministrationRoles,
} from '../../../../utils/roles/client-roles';
import { WelcomeTemplateEntity } from '../../../../../types';

import { useStore } from '../../../state/zustand-store';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../../utils/errors';
import {
  createWelcomeTemplate,
  updateWelcomeTemplate,
  getWelcomeTemplates,
  getUser,
  UserKeys,
} from '../../../../api/users-api';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as analytics from '../../../../shared/analytics/analytics.json';
import dayjs from 'dayjs';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import SimpleBreadcrumbsPage from '../../../../shared/simple-breadcrumbs-page';
import SimpleFormPage from '../../../../shared/simple-form-page';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { pagesConfig } from '../../../../shared/pages-config';
import { useUserSettings } from '../../../../utils/use-user-settings';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../../shared/unsaved-changes-dialog';

import SharedFormStepper, {
  StepConfig,
} from '../../../../shared/shared-form-stepper';
import StepWelcomeTemplateBasicInfo from './form-step/step-welcome-template-basic-info';
import StepWelcomeTemplateSubject from './form-step/step-welcome-template-subject';
import StepWelcomeTemplateBody from './form-step/step-welcome-template-body';
import StepWelcomeTemplateReview from './form-step/step-welcome-template-review-old';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import FormSkeleton from './form-step/form-skeleton';
import Box from '@mui/material/Box';

const getDefaultValues = (
  action: string,
  template: Partial<WelcomeTemplateEntity>
): Partial<WelcomeTemplateEntity> => {
  switch (action) {
    case 'create':
      return {
        createdDate: dayjs(new Date()).valueOf(),
        templateName: '',
        description: '',
        templateSubject: '',
        templateHtml: '',
        default: false,
      };
    case 'update':
      return {
        ...template,
      };
    default:
      return {
        createdDate: dayjs(new Date()).valueOf(),
        templateName: '',
        description: '',
        templateSubject: '',
        templateHtml: '',
        default: false,
      };
  }
};

const BasicInformationSchema = z.object({
  templateName: z.string().min(1, 'Template Name is required.'),
  description: z.string().optional(),
  default: z.boolean().catch(false),
});

const TemplateSubjectSchema = z.object({
  templateSubject: z.string().min(1, 'Email Subject is required.'),
});

const TemplateBodySchema = z.object({
  templateHtml: z.string().min(1, 'Email Body is required.'),
});

const WelcomeTemplateSchema = BasicInformationSchema.and(TemplateSubjectSchema)
  .and(TemplateBodySchema)
  .and(
    z.object({
      createdDate: z.number().catch(0),
      id: z.number().catch(0),
      readonly: z.boolean().catch(false),
    })
  );

export type WelcomeTemplateFormValues = z.infer<typeof WelcomeTemplateSchema>;

export default function WelcomeTemplatesFormPage() {
  const navigate = useNavigate();
  const { changeLogRoles, loggedInUser } = useUserSettings();
  const { id } = useParams();
  const selectedRowId = Number(id);
  const currentAction = id ? 'update' : 'create';
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const location = useLocation();
  const resumeRuleId = (location.state as { resumeRuleId?: number } | null)
    ?.resumeRuleId;

  const { data: welcomeTemplatesData, isLoading: welcomeTemplatesLoading } =
    useQuery({
      queryKey: [UserKeys.EmailTemplates],
      queryFn: () => getWelcomeTemplates(),
      enabled: currentAction === 'update',
    });

  const selectedWelcomeTemplate =
    currentAction === 'update'
      ? welcomeTemplatesData?.results?.find(
          (template) => template.id === selectedRowId
        )
      : undefined;

  const isReadonly =
    currentAction === 'update' && selectedWelcomeTemplate?.readonly === true;

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: [UserKeys.User, loggedInUser],
    queryFn: () => getUser(loggedInUser),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: Partial<WelcomeTemplateEntity>) => {
      if (currentAction === 'update') {
        return updateWelcomeTemplate(data, changeLogMessage);
      } else {
        return createWelcomeTemplate(data, changeLogMessage);
      }
    },

    onSuccess: (returnData, data) => {
      if (resumeRuleId && currentAction === 'create') {
        const resumeUrl = `/${pagesConfig.rulesEngine.link}/${pagesConfig.editRule.link}/${resumeRuleId}`;
        const newTemplate = {
          ...(data as WelcomeTemplateEntity),
          id: returnData.id,
        };
        queryClient.setQueryData(
          [UserKeys.EmailTemplates],
          (oldData: { results: WelcomeTemplateEntity[] } | undefined) => {
            if (!oldData) {
              return { results: [newTemplate] };
            }
            return {
              ...oldData,
              results: [...oldData.results, newTemplate],
            };
          }
        );
        bypass();
        navigate(
          `/${pagesConfig.settings.link}/${pagesConfig.welcomeTemplates.link}`
        );
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Welcome Template created successfully!',
          action: {
            label: 'RESUME RULE CREATION',
            onClick: () => {
              setSnackbar({ open: false, severity: 'success', message: '' });
              navigate(resumeUrl, {
                state: {
                  autoSelectEmailTemplateId: returnData.id,
                  emailSubject: data.templateSubject,
                  emailMessage: data.templateHtml,
                },
              });
            },
          },
        });
        return;
      } else {
        const successMessage =
          currentAction === 'update'
            ? 'Welcome Template updated successfully!'
            : 'Welcome Template added successfully!';

        setSnackbar({
          open: true,
          severity: 'success',
          message: successMessage,
        });
      }

      if (currentAction === 'create') {
        const newTemplate = {
          ...(data as WelcomeTemplateEntity),
          id: returnData.id,
        };

        queryClient.setQueryData(
          [UserKeys.EmailTemplates],
          (oldData: { results: WelcomeTemplateEntity[] }) => {
            const currentTemplates = oldData ? oldData.results : [];
            if (!currentTemplates) return;
            return { ...oldData, results: [...currentTemplates, newTemplate] };
          }
        );
      }

      queryClient.invalidateQueries({
        queryKey: [UserKeys.EmailTemplates],
      });
      setChangeLogMessage('');

      setActiveStep(0);
      reset();
      bypass();
      navigate(
        `/${pagesConfig.settings.link}/${pagesConfig.welcomeTemplates.link}`
      );
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue with the operation. Please refresh and try again.',
      });
    },
    onSettled: () => {
      const actionKey =
        `${currentAction}_welcome_template` as keyof typeof analytics.administration.settings;
      createEvent(analytics.administration.settings[actionKey], {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.settings,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    getValues,
    setValue,
    watch,
    formState: { errors, isValidating, isSubmitting, isDirty },
  } = useForm<WelcomeTemplateFormValues>({
    resolver: zodResolver(WelcomeTemplateSchema),
    defaultValues: getDefaultValues(
      currentAction,
      selectedWelcomeTemplate ?? {}
    ),
  });

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  const handleCancel = () => {
    navigate(
      `/${pagesConfig.settings.link}/${pagesConfig.welcomeTemplates.link}`,
      { state: { fromCancelButton: true } }
    );
  };

  const steps: StepConfig[] = [
    {
      label: 'Basic Information',
      schema: BasicInformationSchema,
    },
    {
      label: 'Email Subject',
      schema: TemplateSubjectSchema,
    },
    {
      label: 'Email Body',
      schema: TemplateBodySchema,
    },
    {
      label: 'Review',
    },
  ];

  const isLoading = welcomeTemplatesLoading || userLoading;

  const [stepCompleted, setStepCompleted] = useState(() =>
    steps.map(() => (selectedRowId ? true : false))
  );

  const formStepperProps = {
    activeStep,
    setActiveStep,
    steps,
    trigger,
    errors,
    stepCompleted,
    setStepCompleted,
  };

  const onSubmit: SubmitHandler<Partial<WelcomeTemplateEntity>> = (data) => {
    const requiredRole =
      currentAction === 'update'
        ? AdministrationRoles.EditWelcomeTemplate.value
        : AdministrationRoles.CreateWelcomeTemplates.value;

    if (isAbleToSeeUIElement(changeLogRoles, requiredRole)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const selectedData = getValues();

  useEffect(() => {
    if (selectedWelcomeTemplate) {
      reset(getDefaultValues(currentAction, selectedWelcomeTemplate));
    }
  }, [selectedWelcomeTemplate, currentAction]);

  useEffect(() => {
    setStepCompleted((prev) => {
      if (prev.length === steps.length) return prev;

      return steps.map((step, idx) => {
        if (idx < prev.length) return prev[idx];
        if (step.label === 'Review') return false;
        return selectedRowId ? true : false;
      });
    });
  }, [steps, selectedRowId]);

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.administration.name },
        { label: pagesConfig.settings.name },
        {
          label: pagesConfig.welcomeTemplates.name,
          path: `${pagesConfig.settings.link}/${pagesConfig.welcomeTemplates.link}`,
        },
      ]}
    >
      <SimpleFormPage
        title={
          currentAction === 'update'
            ? pagesConfig.editWelcomeTemplatesFormPage.name
            : pagesConfig.welcomeTemplatesFormPage.name
        }
        stepper={
          <SharedFormStepper<WelcomeTemplateFormValues> {...formStepperProps} />
        }
        {...formStepperProps}
        content={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            }}
          >
            {isLoading && <FormSkeleton />}
            {!isLoading && steps[activeStep].label === 'Basic Information' && (
              <StepWelcomeTemplateBasicInfo
                control={control}
                errors={errors}
                disabled={isReadonly}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Email Subject' && (
              <StepWelcomeTemplateSubject
                control={control}
                errors={errors}
                watch={watch}
                user={user}
                setValue={setValue}
                getValues={getValues}
                disabled={isReadonly}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Email Body' && (
              <StepWelcomeTemplateBody
                control={control}
                errors={errors}
                watch={watch}
                user={user}
                disabled={isReadonly}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Review' && (
              <StepWelcomeTemplateReview selectedTemplate={selectedData} />
            )}
          </Box>
        }
        handleDiscard={handleCancel}
        handleSubmit={handleSubmit(onSubmit)}
        submitButtonText={
          currentAction === 'update'
            ? 'Update Welcome Template'
            : 'Create Welcome Template'
        }
        isLoadingSubmit={isValidating || isSubmitting || isPending}
        isDisableSubmit={isLoading}
      />

      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Welcome Template"
        pageMode={currentAction}
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          mutate(getValues());
        }}
        mutateLoading={isPending}
      />
    </SimpleBreadcrumbsPage>
  );
}
